/**
 * 支付管理 - 真支付订单 + 通道配置入口
 *
 * 数据源:/api/payment/orders(真支付)
 * 配置入口:跳转到 系统设置 → 支付通道
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Search, Download, CreditCard, TrendingUp, Calendar, Filter,
  Settings, RefreshCw, ExternalLink, CheckCircle, XCircle, Clock,
  AlertCircle, Hash, Smartphone
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

const STATUS_MAP = {
  pending: { label: '待支付', color: 'bg-yellow-500/20 text-yellow-300' },
  paid: { label: '已支付', color: 'bg-green-500/20 text-green-300' },
  failed: { label: '失败', color: 'bg-red-500/20 text-red-300' },
  expired: { label: '已过期', color: 'bg-gray-500/20 text-gray-300' },
};

const METHOD_MAP = {
  mock: { label: '🧪 Mock', color: 'text-yellow-300' },
  alipay_h5: { label: '💙 支付宝 H5', color: 'text-cyan-300' },
  alipay_face: { label: '💙 支付宝扫码', color: 'text-cyan-300' },
  wechat_h5: { label: '💚 微信 H5', color: 'text-green-300' },
  wechat_native: { label: '💚 微信扫码', color: 'text-green-300' },
  self_service: { label: '🤖 自助', color: 'text-purple-300' },
  manual: { label: '👤 人工', color: 'text-gray-300' },
};

export function PaymentsView({ onJumpSettings }) {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid_count: 0, paid_amount: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [search, setSearch] = useState('');

  async function load() {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await api.paymentOrders(params);
      if (r?.ok) {
        let list = r.orders || [];
        if (methodFilter !== 'all') list = list.filter(o => o.method === methodFilter);
        if (search) {
          const s = search.toLowerCase();
          list = list.filter(o =>
            o.id.toLowerCase().includes(s) ||
            (o.phone && o.phone.includes(s)) ||
            (o.name && o.name.includes(s))
          );
        }
        setOrders(list);
        setStats(r.stats || {});
      }
    } catch (e) {
      toast.error('加载失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [statusFilter, methodFilter]);

  const today = Date.now() - 86400000;
  const todayCount = orders.filter(o => o.created_at >= today).length;
  const todayAmount = orders
    .filter(o => o.created_at >= today && o.status === 'paid')
    .reduce((s, o) => s + o.amount, 0);
  const pendingCount = orders.filter(o => o.status === 'pending').length;
  const pendingAmount = orders
    .filter(o => o.status === 'pending')
    .reduce((s, o) => s + o.amount, 0);

  function handleExport() {
    if (orders.length === 0) {
      toast.error('无数据可导出');
      return;
    }
    const csv = [
      ['订单号', '用户', '手机号', '档位', '金额', '方式', '状态', '交易号', '创建时间', '支付时间'],
      ...orders.map(o => [
        o.id, o.name || '-', o.phone || '-', o.tier || '-', o.amount,
        METHOD_MAP[o.method]?.label || o.method,
        STATUS_MAP[o.status]?.label || o.status,
        o.transaction_id || '-',
        new Date(o.created_at).toLocaleString('zh-CN'),
        o.paid_at ? new Date(o.paid_at).toLocaleString('zh-CN') : '-',
      ])
    ].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-orders-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`已导出 ${orders.length} 条订单`);
  }

  return (
    <div className="space-y-3">
      {/* 标题栏 */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <CreditCard size={18} className="text-brand-gold" />
            支付管理
          </h2>
          <div className="text-[10px] text-ink-3 mt-0.5">
            真支付订单(支付通道 → 用户扫码 → 自动到账)
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={load}
            disabled={loading}
            className="text-[10px] px-2 py-1 bg-white/5 hover:bg-white/10 rounded flex items-center gap-1"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            刷新
          </button>
          <button
            onClick={handleExport}
            className="text-[10px] px-2 py-1 bg-white/5 hover:bg-white/10 rounded flex items-center gap-1"
          >
            <Download size={11} />
            导出 CSV
          </button>
          {onJumpSettings && (
            <button
              onClick={() => onJumpSettings('payment')}
              className="text-[10px] px-2 py-1 bg-brand-gold/20 text-brand-gold hover:bg-brand-gold/30 rounded flex items-center gap-1"
            >
              <Settings size={11} />
              支付通道配置
            </button>
          )}
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <SummaryCard
          icon={<TrendingUp size={12} className="text-brand-cyan" />}
          label="累计订单"
          value={stats.total || 0}
          unit="单"
        />
        <SummaryCard
          icon={<CheckCircle size={12} className="text-green-400" />}
          label="累计已付"
          value={`¥${(stats.paid_amount || 0).toFixed(2)}`}
          sub={`${stats.paid_count || 0} 单`}
        />
        <SummaryCard
          icon={<Clock size={12} className="text-yellow-400" />}
          label="待支付"
          value={pendingCount}
          sub={pendingAmount > 0 ? `¥${pendingAmount.toFixed(2)}` : '无'}
        />
        <SummaryCard
          icon={<Calendar size={12} className="text-brand-purple" />}
          label="今日新增"
          value={todayCount}
          sub={todayAmount > 0 ? `¥${todayAmount.toFixed(2)}` : '无收入'}
        />
      </div>

      {/* 筛选器 */}
      <div className="card !p-2 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Filter size={11} className="text-ink-3" />
          <span className="text-[10px] text-ink-3">状态:</span>
          {['all', 'pending', 'paid', 'failed', 'expired'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={clsx(
                'text-[10px] px-2 py-0.5 rounded transition',
                statusFilter === s ? 'bg-brand-cyan text-black font-bold' : 'bg-white/5 hover:bg-white/10 text-ink-3'
              )}
            >
              {s === 'all' ? '全部' : STATUS_MAP[s]?.label || s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-2">
          <span className="text-[10px] text-ink-3">方式:</span>
          <select
            value={methodFilter}
            onChange={e => setMethodFilter(e.target.value)}
            className="text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5"
          >
            <option value="all">全部</option>
            {Object.entries(METHOD_MAP).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1 ml-2 flex-1 max-w-xs">
          <Search size={11} className="text-ink-3" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="订单号 / 手机号 / 用户名"
            className="text-[10px] flex-1 bg-white/5 border border-white/10 rounded px-2 py-0.5"
          />
        </div>
      </div>

      {/* 订单列表 */}
      {loading ? (
        <div className="card text-center py-8 text-ink-3 text-xs">
          <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
          加载中...
        </div>
      ) : orders.length === 0 ? (
        <div className="card text-center py-12">
          <CreditCard size={40} className="mx-auto text-ink-3 mb-2 opacity-30" />
          <div className="text-sm text-ink-3">暂无支付订单</div>
          <div className="text-[10px] text-ink-3 mt-1">
            用户在客户端点"升级"按钮才会创建订单
          </div>
          {onJumpSettings && (
            <button
              onClick={() => onJumpSettings('payment')}
              className="mt-3 text-[10px] px-3 py-1.5 bg-brand-gold/20 text-brand-gold rounded hover:bg-brand-gold/30"
            >
              ⚙️ 配置支付通道
            </button>
          )}
        </div>
      ) : (
        <div className="card !p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/3 text-[10px] text-ink-3">
                <tr>
                  <th className="text-left p-2">订单号</th>
                  <th className="text-left p-2">用户</th>
                  <th className="text-left p-2">档位</th>
                  <th className="text-right p-2">金额</th>
                  <th className="text-left p-2">方式</th>
                  <th className="text-left p-2">状态</th>
                  <th className="text-left p-2">交易号</th>
                  <th className="text-left p-2">创建/支付</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => {
                  const status = STATUS_MAP[o.status] || { label: o.status, color: 'bg-gray-500/20 text-gray-300' };
                  const method = METHOD_MAP[o.method] || { label: o.method, color: 'text-gray-300' };
                  return (
                    <tr key={o.id} className="border-t border-white/5 hover:bg-white/3">
                      <td className="p-2 font-mono text-[10px]">
                        {o.id}
                        <button
                          onClick={() => { navigator.clipboard.writeText(o.id); toast.success('订单号已复制'); }}
                          className="ml-1 text-ink-3 hover:text-brand-cyan"
                        >
                          <Hash size={9} />
                        </button>
                      </td>
                      <td className="p-2">
                        <div>{o.name || '匿名'}</div>
                        <div className="text-[10px] text-ink-3 font-mono">{o.phone || '-'}</div>
                      </td>
                      <td className="p-2">{o.tier}</td>
                      <td className="p-2 text-right font-bold text-brand-gold">¥{o.amount}</td>
                      <td className={clsx('p-2 text-[10px]', method.color)}>{method.label}</td>
                      <td className="p-2">
                        <span className={clsx('text-[10px] px-1.5 py-0.5 rounded', status.color)}>
                          {status.label}
                        </span>
                      </td>
                      <td className="p-2 font-mono text-[10px] text-ink-3 max-w-[120px] truncate">
                        {o.transaction_id || '-'}
                      </td>
                      <td className="p-2 text-[10px] text-ink-3">
                        <div>建:{new Date(o.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                        {o.paid_at && (
                          <div className="text-green-400">
                            付:{new Date(o.paid_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-[10px] text-ink-3 text-center pt-2">
        💡 支付通道配置在 <b>系统设置 → 支付通道</b>(本页面右上角按钮可跳转)
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, unit }) {
  return (
    <div className="card !p-2">
      <div className="flex items-center gap-1.5 text-[10px] text-ink-3">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-lg font-bold text-brand-cyan">
        {value}
        {unit && <span className="text-[10px] text-ink-3 ml-1">{unit}</span>}
      </div>
      {sub && <div className="text-[10px] text-ink-3 mt-0.5">{sub}</div>}
    </div>
  );
}
